import { useGetCurrentUserQuery } from '@carboncopyco/data';
import { DownOutlineIcon } from '@carboncopyco/icons';
import { SearchNoMatchIllustration } from '@carboncopyco/illustrations';
import type { TopAsset } from '@carboncopyco/types';
import {
  Button,
  EmptyView,
  Paper,
  Separator,
  TextBase,
  TextCaption,
  TextLargeParagraph,
  TooltipBubble,
} from '@carboncopyco/ui';
import { MMMDoYYYYhmma, integersFormatter } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { type FC, type ReactElement, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { type Col, FixedTable } from '../FixedTable';
import { AssetNameLink } from './AssetNameLink';
import { TopAssetTableSkeleton } from './TopAssetTableSkeleton';
import { ACTIVITY_TYPES } from './consts';

type TopAsetsTableProps = {
  topAssets: TopAsset[];
  isLoading: boolean;
  renderRow: FC<{
    children: ReactElement;
    key: string;
    item: TopAsset;
  }>;
  title: string;
  controls: ReactElement;
  emptyMessage?: string;
};
export const TopAssetsTable = ({
  topAssets,
  isLoading,
  renderRow,
  title,
  controls,
  emptyMessage,
}: TopAsetsTableProps) => {
  const {
    data: {
      user: { email: currentUserEmail } = {},
    } = {},
  } = useGetCurrentUserQuery();

  const cols: Col<TopAsset>[] = useMemo(
    () => [
      {
        alignment: 'left',
        title: 'Name',
        width: 320,
        maxContentWidth: 260,
        render: (topAsset) => <AssetNameLink topAsset={topAsset} />,
      },
      {
        alignment: 'left',
        title: 'Last activity',
        width: 220,
        maxContentWidth: 160,
        render: (topAsset) => {
          const userLabel =
            topAsset.lastActivity.creatorEmail === currentUserEmail
              ? 'You'
              : topAsset.lastActivity.creatorName ||
                topAsset.lastActivity.creatorEmail ||
                'Anonymous';
          const parsedDatetime = MMMDoYYYYhmma(
            topAsset.lastActivity.updateTime
          );
          const dateTimeParts = parsedDatetime.split('at');
          const date = dateTimeParts[0].trim();
          const time = dateTimeParts[1].trim();
          const activityLabel = `${userLabel} ${ACTIVITY_TYPES[topAsset.lastActivity.action]}`;

          return (
            <>
              <TooltipBubble
                title={activityLabel}
                placement="top-start"
              >
                <div className="truncate text-ds-natural-600">
                  <TextCaption
                    label={activityLabel}
                    color="text-ds-natural-600"
                    lineHeight="leading-ds-200"
                  />
                </div>
              </TooltipBubble>

              <div className="flex flex-col leading-ds-200">
                <TooltipBubble
                  title={parsedDatetime}
                  placement="top-start"
                >
                  <div className="truncate flex items-center gap-1">
                    <TextCaption
                      label={date}
                      color="text-ds-natural-300"
                      ellipsis
                    />
                    <TextCaption label="&bull;" />
                    <TextCaption label={time} />
                  </div>
                </TooltipBubble>
              </div>
            </>
          );
        },
      },
      {
        alignment: 'center',
        title: 'Views',
        width: 40,
        render: (topAsset) => {
          const views =
            topAsset.analyticsData.visits + topAsset.analyticsData.sessions;
          return (
            <TextBase
              label={integersFormatter(views)}
              color="text-ds-natural-600"
            />
          );
        },
      },
    ],

    [currentUserEmail]
  );
  const [isOverflowing, setIsOverflowing] = useBoolean();
  const [isExpanded, setIsExpanded] = useBoolean();
  const tableParentRef = useRef<HTMLDivElement>();

  const [listBottomRef, _, entry] = useInView({
    root: tableParentRef.current,
  });

  useEffect(() => {
    setIsOverflowing.set(!entry?.isIntersecting);
  }, [entry, setIsOverflowing]);

  const isEmpty = !isLoading && !topAssets.length;

  return (
    <Paper
      radius
      border="ds-regular"
      fillParent
    >
      <div className="flex flex-col w-full h-full">
        <div className="flex flex-col grow overflow-hidden">
          <div className="p-5 flex items-center justify-between">
            <TextLargeParagraph
              label={title}
              color="text-ds-natural-600"
            />
            <div className="flex items-center gap-4">{controls}</div>
          </div>
          <Separator horizontal />
          {isEmpty ? (
            <EmptyView
              noBg
              illustration={<SearchNoMatchIllustration />}
              title="No data available"
              message={
                emptyMessage ||
                'Once some demos are run, you’ll see the data here'
              }
            />
          ) : (
            <>
              <div
                ref={tableParentRef}
                className={classNames(
                  'w-full relative',
                  isExpanded ? 'overflow-auto' : 'overflow-hidden'
                )}
              >
                <FixedTable<TopAsset>
                  headerRowClassName="px-6 pt-4 pb-1 sticky top-0 left-0 right-0 bg-ds-accent3-100 z-[5]"
                  cols={cols}
                  data={topAssets}
                  isLoading={isLoading}
                  loader={<TopAssetTableSkeleton />}
                  rowWrapper={renderRow}
                  loaderPlaceholderSpace={12}
                  noWrapperDiv
                />
                <div ref={listBottomRef} />
              </div>
            </>
          )}
        </div>

        {isOverflowing && !isExpanded && !isEmpty && (
          <div className="pt-3 pb-2">
            <Button
              link
              blue
              label="Show more"
              rightIcon={<DownOutlineIcon />}
              onClick={setIsExpanded.setTrue}
            />
          </div>
        )}
      </div>
    </Paper>
  );
};
